import React, { useState, useRef, useEffect } from 'react';
import { 
  X, 
  Clock, 
  CheckCircle, 
  ThumbsUp, 
  RefreshCw,
  MessageSquare,
  User,
  Calendar,
  FileText
} from 'lucide-react';
import './BrandFeedbackModal.css';
import VideoSelectionModal from './VideoSelectionModal';
import RevisionRequestModal from './RevisionRequestModal';
import SubmissionService from '../services/submissionService';
import SubmissionVideoPlayer from './SubmissionVideoPlayer';
import { addBusinessDays, REVISION_DEADLINE_BUSINESS_DAYS } from '../utils/dateUtils';
import { formatVideoTime, parseVideoTimestamp } from '../utils/videoPlayerUtils';
import toast from 'react-hot-toast';

const BrandFeedbackModal = ({ 
  isOpen, 
  onClose, 
  submission, 
  onApprove,
  onRequestRevision
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [timestampedNotes, setTimestampedNotes] = useState([]);
  const [newTimestampNote, setNewTimestampNote] = useState('');
  const [activeVideoTab, setActiveVideoTab] = useState(0);
  const [selectedHook, setSelectedHook] = useState(0);
  const [selectedCTA, setSelectedCTA] = useState(0);
  const [showVideoSelection, setShowVideoSelection] = useState(false);
  const [showRevisionRequest, setShowRevisionRequest] = useState(false);
  const [selectedVideosForRevision, setSelectedVideosForRevision] = useState([]);
  const [showSafeZone, setShowSafeZone] = useState(false);
  const [revisionCurrentTime, setRevisionCurrentTime] = useState(0);
  
  const playerRef = useRef(null);
  const revisionPlayerRef = useRef(null);

  // Initialize modal state
  useEffect(() => {
    if (isOpen) {
      setShowApprovalConfirm(false);
      setShowRevisionModal(false);
      setRevisionNotes('');
      setTimestampedNotes([]);
      setNewTimestampNote('');
      setActiveVideoTab(0);
      setSelectedHook(0);
      setSelectedCTA(0);
    }
  }, [isOpen, submission]);

  const seekToTimestamp = (timestamp) => {
    playerRef.current?.seekTo(parseVideoTimestamp(timestamp));
  };

  const handleApprove = () => {
    setShowApprovalConfirm(true);
  };

  const confirmApproval = async () => {
    setIsSubmitting(true);
    try {
      const originalData = submission._originalData;
      
      if (!originalData) {
        throw new Error('Submission data not found');
      }

      // Approve all videos in the submission
      await SubmissionService.approveAllVideos(
        originalData.campaignId,
        originalData.briefId,
        submission.id
      );

      toast.success('Content approved successfully!');
      
      if (onApprove) onApprove(submission.id);
      onClose();
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('Failed to approve content: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestRevision = () => {
    // If only one video, go straight to revision modal
    if (submission.content.length === 1) {
      setSelectedVideosForRevision([submission.content[0]]);
      setShowRevisionRequest(true);
    } else {
      // Multiple videos - show selection modal
      setShowVideoSelection(true);
    }
  };

  const handleVideoSelectionConfirm = (selectedVideos) => {
    setSelectedVideosForRevision(selectedVideos);
    setShowVideoSelection(false);
    setShowRevisionRequest(true);
  };

  const handleRevisionSubmit = async (revisionRequest) => {
    // Don't submit immediately - show confirmation modal first
    // Close the revision request modal
    setShowRevisionRequest(false);
    
    // Pass the revision request data to parent to show confirmation modal
    if (onRequestRevision) {
      onRequestRevision(submission, revisionRequest);
    }
  };

  const addTimestampNote = () => {
    if (newTimestampNote.trim() && revisionPlayerRef.current) {
      const timestamp = Math.floor(revisionPlayerRef.current.getCurrentTime());
      const newNote = {
        id: Date.now(),
        timestamp: timestamp,
        note: newTimestampNote.trim()
      };
      setTimestampedNotes([...timestampedNotes, newNote]);
      setNewTimestampNote('');
    }
  };

  const removeTimestampNote = (id) => {
    setTimestampedNotes(timestampedNotes.filter(note => note.id !== id));
  };

  const submitRevisionRequest = async () => {
    setIsSubmitting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (onRequestRevision) onRequestRevision(submission.id, {
        generalNotes: revisionNotes,
        timestampedNotes: timestampedNotes
      });
      onClose();
    } catch (error) {
      console.error('Error requesting revision:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !submission) return null;

  // Get the current video's URL (use downloadURL, fallback to videoUrl)
  const getCurrentVideoUrl = () => {
    const video = submission.content[activeVideoTab];
    return video?.downloadURL || video?.videoUrl;
  };

  return (
    <div className="brand-feedback-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="brand-feedback-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="brand-feedback-modal-header">
          <div className="brand-feedback-modal-title-section">
            <div className="brand-feedback-creator-info">
              <div className="brand-feedback-creator-avatar">
                <img 
                  src={submission.creator.avatar} 
                  alt={submission.creator.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="brand-feedback-avatar-fallback">
                  {submission.creator.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
              </div>
              <div className="brand-feedback-creator-details">
                <h3 className="brand-feedback-modal-title">Review Content Submission</h3>
                <p className="brand-feedback-modal-subtitle">
                  {submission.creator.name} - {submission.campaign}
                </p>
              </div>
            </div>
          </div>
          <button className="brand-feedback-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="brand-feedback-modal-content">
          <div className="brand-feedback-review-section">
            {/* Video Preview */}
            <div className="brand-feedback-video-section">
              {/* Video Selectors - Side by Side */}
              <div className="brand-feedback-selectors-row">
                {/* Video Selector - Shows all main videos */}
                <div className="brand-feedback-video-selector">
                  <label className="brand-feedback-select-label">Video</label>
                  <select 
                    value={activeVideoTab} 
                    onChange={(e) => setActiveVideoTab(parseInt(e.target.value))}
                    className="brand-feedback-video-dropdown"
                  >
                    {submission.content
                      .filter(video => !video.variationType) // Only main videos
                      .map((video) => {
                        const originalIndex = submission.content.findIndex(v => v.id === video.id);
                        return (
                          <option key={video.id} value={originalIndex}>
                            {video.title}
                          </option>
                        );
                      })}
                  </select>
                </div>

                {/* Variation Selector - Only show if current video has variations */}
                {(() => {
                  const currentVideo = submission.content[activeVideoTab];
                  
                  // Check if current video has variations (other videos with same type and variationType)
                  const hasVariations = submission.content.some(video => 
                    video.type === currentVideo.type && 
                    video.variationType && 
                    video.id !== currentVideo.id
                  );
                  
                  if (!hasVariations) return null;
                  
                  // Get all videos of the same type (including current video and variations)
                  const sameTypeVideos = submission.content.filter(video => 
                    video.type === currentVideo.type
                  );
                  
                  return (
                    <div className="brand-feedback-video-selector">
                      <label className="brand-feedback-select-label">Variation</label>
                      <select 
                        value={activeVideoTab} 
                        onChange={(e) => setActiveVideoTab(parseInt(e.target.value))}
                        className="brand-feedback-video-dropdown"
                      >
                        {sameTypeVideos.map((video) => {
                          const originalIndex = submission.content.findIndex(v => v.id === video.id);
                          return (
                            <option key={video.id} value={originalIndex}>
                              {video.title}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                })()}
              </div>
              
              
              <SubmissionVideoPlayer
                ref={playerRef}
                video={submission.content[activeVideoTab]}
                videoUrl={getCurrentVideoUrl()}
                showSafeZone={false}
                badge={(() => {
                  const currentVideo = submission.content[activeVideoTab];
                  const isLatestRevision = currentVideo?.revisionCount === submission.revisionCount;
                  return isLatestRevision && submission.status === 'revision_submission' ? (
                    <div className="brand-feedback-revision-indicator">
                      <CheckCircle size={16} />
                      <span>Latest Revision</span>
                    </div>
                  ) : null;
                })()}
              />
              
              
            </div>

            {/* Video-Specific Notes */}
            <div className="brand-feedback-notes-section">
              <h4 className="brand-feedback-section-title">Creator Notes - {submission.content[activeVideoTab].title}</h4>
              
              {/* Video-Specific Notes */}
              {submission.content[activeVideoTab].notes && (
                <div className="brand-feedback-creator-note">
                  <div className="brand-feedback-note-header">
                    <FileText size={16} />
                    <span>Video Notes</span>
                  </div>
                  <p className="brand-feedback-note-content">{submission.content[activeVideoTab].notes}</p>
                </div>
              )}

              {/* Video-Specific Timestamped Notes */}
              {submission.content[activeVideoTab].timestampedNotes && submission.content[activeVideoTab].timestampedNotes.length > 0 && (
                <div className="brand-feedback-timestamped-notes">
                  <div className="brand-feedback-note-header">
                    <Clock size={16} />
                    <span>Timestamped Notes</span>
                  </div>
                  <div className="brand-feedback-timestamp-list">
                    {submission.content[activeVideoTab].timestampedNotes.map((note, index) => (
                      <div 
                        key={index} 
                        className="brand-feedback-timestamp-item"
                        onClick={() => seekToTimestamp(note.timestamp || note.time)}
                        style={{ cursor: 'pointer' }}
                        title="Click to seek to this timestamp"
                      >
                        <div className="brand-feedback-timestamp-time">
                          {note.timestamp || (note.time ? formatVideoTime(note.time) : '0:00')}
                        </div>
                        <div className="brand-feedback-timestamp-content">{note.note}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submission Details */}
              <div className="brand-feedback-submission-details">
                <div className="brand-feedback-detail-item">
                  <User size={16} />
                  <span><strong>Creator:</strong> {submission.creator.name} ({submission.creator.handle})</span>
                </div>
                <div className="brand-feedback-detail-item">
                  <Calendar size={16} />
                  <span><strong>Submitted:</strong> {new Date(submission.submittedAt).toLocaleDateString()}</span>
                </div>
                <div className="brand-feedback-detail-item">
                  <MessageSquare size={16} />
                  <span><strong>Campaign:</strong> {submission.campaign}</span>
                </div>
                <div className="brand-feedback-detail-item">
                  <RefreshCw size={16} />
                  <span><strong>Revisions:</strong> {submission.revisionCount || 0}/2</span>
                </div>
              </div>

              {/* Revision History */}
              {(submission.revisionCount > 0 || submission.status === 'revision_requested' || submission.status === 'needs_revision') && (
                <div className="brand-feedback-revision-history">
                  <h4>Revision History</h4>
                  <div className="revision-timeline">
                    {/* Initial Submission */}
                    <div className="revision-item revision-submitted">
                      <div className="revision-icon">
                        <CheckCircle size={16} />
                      </div>
                      <div className="revision-content">
                        <div className="revision-title">Initial Submission</div>
                        <div className="revision-date">{new Date(submission.submittedAt).toLocaleDateString()}</div>
                        <div className="revision-status status-submitted">Submitted</div>
                      </div>
                    </div>

                    {/* Revision Requests */}
                    {submission.revisionCount > 0 && (
                      <>
                        {Array.from({ length: submission.revisionCount }, (_, index) => (
                          <div key={`revision-${index + 1}`} className="revision-item revision-requested">
                            <div className="revision-icon">
                              <RefreshCw size={16} />
                            </div>
                            <div className="revision-content">
                              <div className="revision-title">Revision Request #{index + 1}</div>
                              <div className="revision-date">
                                {submission.lastRevisionRequestedAt 
                                  ? new Date(submission.lastRevisionRequestedAt).toLocaleDateString()
                                  : 'Recently requested'
                                }
                              </div>
                              <div className="revision-status status-revision-requested">Revision Requested</div>
                              {submission.generalFeedback && (
                                <div className="revision-feedback">
                                  <strong>Feedback:</strong> {submission.generalFeedback}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Current Status */}
                    {(submission.status === 'revision_requested' || submission.status === 'needs_revision') && (
                      <div className="revision-item revision-pending">
                        <div className="revision-icon">
                          <Clock size={16} />
                        </div>
                        <div className="revision-content">
                          <div className="revision-title">Awaiting Revision</div>
                          <div className="revision-date">
                            {(() => {
                              const revisionDate = submission.lastRevisionRequestedAt
                                || submission._originalData?.lastRevisionRequestedAt
                                || submission.submittedAt;
                              const revisionDateObj = revisionDate?.toDate?.() ?? (revisionDate instanceof Date ? revisionDate : new Date(revisionDate));
                              const deadline = addBusinessDays(new Date(revisionDateObj), REVISION_DEADLINE_BUSINESS_DAYS);
                              const now = new Date();
                              const timeLeft = deadline - now;
                              const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));
                              
                              if (daysLeft <= 0) {
                                return 'Overdue';
                              } else if (daysLeft === 1) {
                                return '1 day remaining';
                              } else {
                                return `${daysLeft} days remaining`;
                              }
                            })()}
                          </div>
                          <div className="revision-status status-pending">
                            {(() => {
                              const revisionDate = submission.lastRevisionRequestedAt
                                || submission._originalData?.lastRevisionRequestedAt
                                || submission.submittedAt;
                              const revisionDateObj = revisionDate?.toDate?.() ?? (revisionDate instanceof Date ? revisionDate : new Date(revisionDate));
                              const deadline = addBusinessDays(new Date(revisionDateObj), REVISION_DEADLINE_BUSINESS_DAYS);
                              const now = new Date();
                              const timeLeft = deadline - now;
                              const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));
                              
                              if (daysLeft <= 0) {
                                return 'Overdue';
                              } else {
                                return 'Awaiting Creator Response';
                              }
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="brand-feedback-modal-footer">
          <div className="brand-feedback-modal-actions">
            <button 
              className="brand-feedback-cancel-btn"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            
            <div className="brand-feedback-action-buttons">
              <button 
                className="brand-feedback-approve-btn"
                onClick={handleApprove}
                disabled={isSubmitting}
              >
                <ThumbsUp size={16} />
                Approve
              </button>
              
              <button 
                className="brand-feedback-revision-btn"
                onClick={handleRequestRevision}
                disabled={isSubmitting}
              >
                <RefreshCw size={16} />
                Request Revision
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Approval Confirmation Modal */}
      {showApprovalConfirm && (
        <div className="brand-feedback-confirm-overlay">
          <div className="brand-feedback-confirm-modal">
            <div className="brand-feedback-confirm-header">
              <h3>Confirm Approval</h3>
              <button onClick={() => setShowApprovalConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="brand-feedback-confirm-content">
              <p>Are you sure you want to approve this submission?</p>
              <p className="brand-feedback-confirm-subtitle">
                This action will mark the content as approved and notify the creator.
              </p>
            </div>
            <div className="brand-feedback-confirm-actions">
              <button 
                className="brand-feedback-confirm-cancel"
                onClick={() => setShowApprovalConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="brand-feedback-confirm-approve"
                onClick={confirmApproval}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Approving...' : 'Yes, Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revision Feedback Modal */}
      {showRevisionModal && (
        <div className="brand-feedback-revision-overlay">
          <div className="brand-feedback-revision-modal">
            <div className="brand-feedback-revision-header">
              <h3>Request Revision</h3>
              <button onClick={() => setShowRevisionModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="brand-feedback-revision-content">
              {/* Video Section */}
              <div className="brand-feedback-revision-video-section">
                {/* Video Selector */}
                {submission.content && submission.content.length > 1 && (
                  <div className="brand-feedback-video-selector">
                    <label className="brand-feedback-select-label">Video</label>
                    <select 
                      value={activeVideoTab} 
                      onChange={(e) => setActiveVideoTab(parseInt(e.target.value))}
                      className="brand-feedback-video-dropdown"
                    >
                      {submission.content.map((video, index) => (
                        <option key={video.id} value={index}>
                          Video {index + 1}: {video.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                
                <SubmissionVideoPlayer
                  ref={revisionPlayerRef}
                  video={submission.content[activeVideoTab]}
                  videoUrl={getCurrentVideoUrl()}
                  showSafeZone={showSafeZone}
                  onTimeUpdate={setRevisionCurrentTime}
                />
                <div className="brand-feedback-video-controls-row">
                  <label className="brand-feedback-safe-zone-toggle-switch">
                    <input
                      type="checkbox"
                      checked={showSafeZone}
                      onChange={(e) => setShowSafeZone(e.target.checked)}
                    />
                    <span className="brand-feedback-toggle-slider"></span>
                    <span className="brand-feedback-toggle-label">Show Safe Zone</span>
                  </label>
                </div>
              </div>

              {/* Notes Section */}
              <div className="brand-feedback-revision-notes-section">
                {/* General Notes */}
                <div className="brand-feedback-notes-section">
                  <h4>General Feedback</h4>
                  <textarea
                    value={revisionNotes}
                    onChange={(e) => setRevisionNotes(e.target.value)}
                    placeholder="Provide general feedback for the creator..."
                    className="brand-feedback-notes-textarea"
                  />
                </div>

                {/* Timestamped Notes */}
                <div className="brand-feedback-timestamp-section">
                  <h4>Timestamped Notes</h4>
                  <div className="brand-feedback-timestamp-input">
                    <input
                      type="text"
                      value={newTimestampNote}
                      onChange={(e) => setNewTimestampNote(e.target.value)}
                      placeholder="Add note for current timestamp..."
                      className="brand-feedback-timestamp-input-field"
                    />
                    <button 
                      onClick={addTimestampNote}
                      className="brand-feedback-add-timestamp-btn"
                    >
                      <Clock size={16} />
                      Add Note ({formatVideoTime(revisionCurrentTime)})
                    </button>
                  </div>
                  
                  {timestampedNotes.length > 0 && (
                    <div className="brand-feedback-timestamp-list">
                      {timestampedNotes.map((note) => (
                        <div key={note.id} className="brand-feedback-timestamp-item">
                          <span className="brand-feedback-timestamp-time">
                            {formatVideoTime(note.timestamp)}
                          </span>
                          <span className="brand-feedback-timestamp-text">{note.note}</span>
                          <button 
                            onClick={() => removeTimestampNote(note.id)}
                            className="brand-feedback-remove-timestamp"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="brand-feedback-revision-footer">
              <button 
                className="brand-feedback-revision-cancel"
                onClick={() => setShowRevisionModal(false)}
              >
                Cancel
              </button>
              <button 
                className="brand-feedback-revision-submit"
                onClick={submitRevisionRequest}
                disabled={isSubmitting || (!revisionNotes.trim() && timestampedNotes.length === 0)}
              >
                {isSubmitting ? 'Sending...' : 'Send Revision Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Selection Modal */}
      <VideoSelectionModal
        isOpen={showVideoSelection}
        onClose={() => setShowVideoSelection(false)}
        submission={submission}
        onConfirm={handleVideoSelectionConfirm}
      />

      {/* Revision Request Modal */}
      <RevisionRequestModal
        isOpen={showRevisionRequest}
        onClose={() => setShowRevisionRequest(false)}
        submission={submission}
        selectedVideos={selectedVideosForRevision}
        onSubmit={handleRevisionSubmit}
      />
    </div>
  );
};

export default BrandFeedbackModal;

